import React, { useState, useEffect, useRef } from 'react';
import { X, Heart, MapPin, Phone, Star, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Result } from '../types/chat';
import { useSavedPlaces } from '../contexts/SavedPlacesContext';
import { PlaceItem } from '../types/explore';
import { getImageVariants } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import RegistrationPrompt from './RegistrationPrompt';

interface PlaceDetailPanelProps {
  place: Result | PlaceItem;
  onClose: () => void;
  onLoadFullDetails?: (placeId: string) => Promise<void>;
  isLoadingDetails?: boolean;
}

const PlaceDetailPanel: React.FC<PlaceDetailPanelProps> = ({ place, onClose, onLoadFullDetails, isLoadingDetails = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const { isPlaceSaved, savePlace, unsavePlace } = useSavedPlaces();
  const { authState } = useAuth();
  const { canSave } = usePermissions();
  const user = authState.user;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragStartYRef = useRef(0);
  
  // Check if place data is incomplete (missing key detail fields)
  const isIncompleteData = !place.entity_type || !place.pros1 || !place.cons1;

  // Get all available photos
  const photos = [
    place.photo_1_cdn,
    place.photo_2_cdn,
    place.photo_3_cdn,
    place.photo_4_cdn,
    place.photo_5_cdn,
  ].filter(Boolean) as string[];

  // Helper function to format array or string fields with proper spacing
  const formatArrayField = (field: any): string => {
    if (!field) return '';
    
    // If it's already an array
    if (Array.isArray(field)) {
      return field.join(', ');
    }
    
    // If it's a string that looks like a JSON array
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        if (Array.isArray(parsed)) {
          return parsed.join(', ');
        }
      } catch (e) {
        // Not a JSON string, continue with other checks
      }
      
      // If it's a comma-separated string without spaces
      if (field.includes(',') && !field.includes(', ')) {
        return field.split(',').join(', ');
      }
      
      // Return as-is if already formatted or single value
      return field;
    }
    
    return String(field);
  };

  // Helper function to parse array fields (returns actual array for .map())
  const parseArrayField = (field: any): any[] => {
    if (!field) return [];
    
    // If it's already an array
    if (Array.isArray(field)) {
      return field;
    }
    
    // If it's a string that looks like a JSON array
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (e) {
        // Not a JSON string, return as single-item array
        return [field];
      }
    }
    
    return [];
  };

  // Use the full place object for saving (preserves all 67 fields)
  const placeId = place.baseline_id || place.id || '';
  const isSaved = isPlaceSaved(placeId);

  // Handle save/unsave
  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check permissions before saving (unsaving is always allowed)
    if (!isSaved && !canSave().allowed) {
      setShowRegistrationPrompt(true);
      return;
    }
    
    setIsAnimating(true);

    try {
      if (isSaved) {
        await unsavePlace(placeId);
      } else {
        // Ensure the place object has the id field set correctly
        // (Result type from chat may have baseline_id instead of id)
        const placeToSave = {
          ...place,
          id: placeId // Ensure id is set from baseline_id or id
        } as PlaceItem;
        
        await savePlace(placeToSave);
      }
    } catch (error) {
      // Silently handle error
    } finally {
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  // Carousel navigation
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % photos.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Prevent scroll chaining to underlying content
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      scrollContainer.dataset.touchStartY = touch.clientY.toString();
      scrollContainer.dataset.scrollTopStart = scrollContainer.scrollTop.toString();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const startY = parseFloat(scrollContainer.dataset.touchStartY || '0');
      const deltaY = touch.clientY - startY;
      
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;
      const isScrollable = scrollHeight > clientHeight;

      if (!isScrollable) {
        // Not scrollable - prevent all scrolling
        e.preventDefault();
        return;
      }

      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // Prevent pull-to-refresh when at top and scrolling down
      if (isAtTop && deltaY > 0) {
        e.preventDefault();
      }
      
      // Prevent scroll chaining when at bottom and scrolling up
      if (isAtBottom && deltaY < 0) {
        e.preventDefault();
      }
    };

    scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollContainer.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      scrollContainer.removeEventListener('touchstart', handleTouchStart);
      scrollContainer.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const getEntityTypeLabel = () => {
    switch (place.entity_type) {
      case 'hotel': return 'Hotel';
      case 'food': return 'Restaurant';
      case 'attraction': return 'Attraction';
      case 'experience': return 'Experience';
      default: return 'Place';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed z-50 bg-white shadow-2xl overflow-hidden flex flex-col
                   md:right-0 md:top-0 md:bottom-0 md:w-[50%] md:rounded-l-2xl md:animate-slide-in-right md:max-h-full
                   max-md:left-0 max-md:right-0 max-md:rounded-t-3xl max-md:animate-slide-up"
        style={{
          // Mobile: push panel down to account for safe areas + extra spacing
          top: 'calc(max(0px, env(safe-area-inset-top)) + 8px)',
          bottom: 'auto',
          // Mobile: account for safe areas (notch/home indicator)
          // max-height = 100vh - bottom nav (60px) - safe-area-inset-top - safe-area-inset-bottom
          maxHeight: 'calc(100vh - 60px - max(0px, env(safe-area-inset-top)) - max(0px, env(safe-area-inset-bottom)))',
          transform: `translateY(${translateY}px)`,
          transition: isClosing ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
        }}
      >
        {/* Header with drag-to-close support */}
        <div 
          className="flex-shrink-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10 max-md:touch-none max-md:cursor-grab max-md:active:cursor-grabbing"
          style={{ touchAction: 'none' }}
          onTouchStart={(e) => {
            dragStartYRef.current = e.touches[0].clientY;
          }}
          onTouchMove={(e) => {
            if (e.touches.length === 1 && !isClosing) {
              const currentY = e.touches[0].clientY;
              const deltaY = currentY - dragStartYRef.current;
              
              // Only allow dragging down (positive deltaY)
              if (deltaY > 0) {
                setTranslateY(deltaY);
              }
            }
          }}
          onTouchEnd={() => {
            // If dragged more than 60px, close the panel smoothly
            if (translateY > 60) {
              setIsClosing(true);
              setTranslateY(window.innerHeight);
              setTimeout(() => {
                onClose();
              }, 400); // Match the transition duration
            } else {
              // Snap back to original position
              setTranslateY(0);
            }
          }}
        >
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">{place.title}</h2>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                {getEntityTypeLabel()}
              </span>
              {place.city && (
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {place.city}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Save Button */}
            <button
              onClick={handleSaveClick}
              className={`p-2 rounded-full transition-all duration-300 ${
                isSaved
                  ? 'bg-red-500 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${isAnimating ? 'animate-pulse scale-110' : ''}`}
            >
              <Heart
                size={20}
                fill={isSaved ? 'currentColor' : 'none'}
                className={`transition-all duration-300 ${isAnimating ? 'scale-125' : ''}`}
              />
            </button>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto overscroll-y-contain md:pb-4" 
          style={{ 
            WebkitOverflowScrolling: 'touch',
            // Mobile: dynamic bottom padding for safe area (home indicator on Safari)
            paddingBottom: 'calc(max(0px, env(safe-area-inset-bottom)) + 5rem)'
          }}
        >
          {/* Image Carousel */}
          {photos.length > 0 && (
            <div className="relative h-[512px] bg-gray-100">
              <img
                src={(() => {
                  const variants = getImageVariants(photos[currentImageIndex]);
                  return variants.medium || variants.original || photos[currentImageIndex];
                })()}
                alt={`${place.title} - ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder-image.jpg';
                }}
              />
              {photos.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-all"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-80 rounded-full hover:bg-opacity-100 transition-all"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {photos.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentImageIndex
                            ? 'bg-white w-6'
                            : 'bg-white bg-opacity-50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
              
              {/* Load Full Details Button */}
              {isIncompleteData && onLoadFullDetails && (
                <div className="absolute bottom-4 left-4 right-4">
                  <button
                    onClick={() => onLoadFullDetails(place.id || place.baseline_id || '')}
                    disabled={isLoadingDetails}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isLoadingDetails ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Loading Full Details...</span>
                      </>
                    ) : (
                      <>
                        <ExternalLink size={18} />
                        <span>Load Full Details</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Details */}
          <div className="p-4 space-y-4">
            {/* Rating */}
            {place.rating && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-yellow-500">
                  <Star size={18} fill="currentColor" />
                  <span className="font-semibold text-gray-900">
                    {typeof place.rating === 'number' ? place.rating.toFixed(1) : place.rating}
                  </span>
                </div>
                <span className="text-gray-500 text-sm">Rating</span>
              </div>
            )}

            {/* Google Maps Link */}
            {place.location_link && (
              <a
                href={place.location_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <MapPin size={18} />
                <span className="font-medium">View on Google Maps</span>
                <ExternalLink size={16} className="ml-auto" />
              </a>
            )}

            {/* Phone */}
            {place.phone && place.phone !== 'null' && place.phone !== 'undefined' && place.phone.trim() !== '' && (
              <div className="flex items-center gap-2 text-gray-700">
                <Phone size={18} />
                <a href={`tel:${place.phone}`} className="hover:underline">
                  {place.phone}
                </a>
              </div>
            )}

            {/* Website */}
            {place.website && place.website !== 'null' && place.website !== 'undefined' && place.website.trim() !== '' && (
              <a
                href={place.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
              >
                <ExternalLink size={18} />
                <span>Visit Website</span>
              </a>
            )}

            {/* Address */}
            {place.address && place.address !== 'null' && place.address !== 'undefined' && place.address.trim() !== '' && (
              <div className="text-sm text-gray-600 flex items-start gap-2">
                <MapPin size={18} className="flex-shrink-0 mt-0.5" />
                <span>{place.address}</span>
              </div>
            )}

            {/* Hotel Specific Fields */}
            {place.entity_type === 'hotel' && (
              <>
                {place.price_per_night_dec && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Price per night</div>
                    <div className="text-2xl font-bold text-green-700">
                      ${place.price_per_night_dec}
                    </div>
                  </div>
                )}
                
                {/* Hotel Star Rating */}
                {place.hotel_star && (
                  <div className="flex items-center gap-1">
                    <Star size={18} className="text-yellow-500" fill="currentColor" />
                    <span className="font-semibold">{place.hotel_star}-Star Hotel</span>
                  </div>
                )}
                
                {place.amenities && parseArrayField(place.amenities).length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Amenities</h3>
                    <div className="flex flex-wrap gap-2">
                      {parseArrayField(place.amenities).map((amenity, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {place.stay_description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Stay Experience</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.stay_description}</p>
                  </div>
                )}
                {place.food_description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Dining</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.food_description}</p>
                  </div>
                )}
                {place.location_description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.location_description}</p>
                  </div>
                )}
              </>
            )}

            {/* Food Specific Fields */}
            {place.entity_type === 'food' && (
              <>
                {(place.cost_per_person_min || place.cost_per_person_max) && (
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Cost per person</div>
                    <div className="text-lg font-bold text-orange-700">
                      {place.cost_per_person_min && place.cost_per_person_max
                        ? `${place.cost_per_person_min} - ${place.cost_per_person_max} LKR`
                        : place.cost_per_person_min
                        ? `From ${place.cost_per_person_min} LKR`
                        : `Up to ${place.cost_per_person_max} LKR`}
                    </div>
                  </div>
                )}
                {place.cuisine && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Cuisine</h3>
                    <p className="text-gray-700">{formatArrayField(place.cuisine)}</p>
                  </div>
                )}
                {place.ambience && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Ambience</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.ambience}</p>
                  </div>
                )}
                {place.signature_dishes && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Signature Dishes</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{formatArrayField(place.signature_dishes)}</p>
                  </div>
                )}
                {place.dietary_options && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Dietary Options</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{formatArrayField(place.dietary_options)}</p>
                  </div>
                )}
                {place.food_description && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">About</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.food_description}</p>
                  </div>
                )}
              </>
            )}

            {/* Experience Specific Fields */}
            {place.entity_type === 'experience' && (
              <>
                {place.cultural_significance && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">About This Experience</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.cultural_significance}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {place.typical_time_spent && (
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Typical Duration</div>
                      <div className="font-semibold text-gray-800 text-sm">{place.typical_time_spent}</div>
                    </div>
                  )}
                  {place.difficulty && (
                    <div className="bg-red-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Difficulty</div>
                      <div className="font-semibold text-red-700 text-sm">{place.difficulty}</div>
                    </div>
                  )}
                  {place.best_season && (
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Best Season</div>
                      <div className="font-semibold text-green-700 text-sm">{formatArrayField(place.best_season)}</div>
                    </div>
                  )}
                  {place.best_time_of_day && (
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Best Time</div>
                      <div className="font-semibold text-yellow-700 text-sm">{formatArrayField(place.best_time_of_day)}</div>
                    </div>
                  )}
                  {place.crowd_level && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Crowd Level</div>
                      <div className="font-semibold text-blue-700 text-sm">{place.crowd_level}</div>
                    </div>
                  )}
                </div>
                {place.season_notes && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Seasonal Notes</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.season_notes}</p>
                  </div>
                )}
              </>
            )}

            {/* Attraction Specific Fields */}
            {place.entity_type === 'attraction' && (
              <>
                {place.cultural_significance && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.cultural_significance}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {place.typical_time_spent && (
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Typical Duration</div>
                      <div className="font-semibold text-gray-800 text-sm">{place.typical_time_spent}</div>
                    </div>
                  )}
                  {place.difficulty && (
                    <div className="bg-red-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Difficulty</div>
                      <div className="font-semibold text-red-700 text-sm">{place.difficulty}</div>
                    </div>
                  )}
                  {place.best_season && (
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Best Season</div>
                      <div className="font-semibold text-green-700 text-sm">{formatArrayField(place.best_season)}</div>
                    </div>
                  )}
                  {place.best_time_of_day && (
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Best Time</div>
                      <div className="font-semibold text-yellow-700 text-sm">{formatArrayField(place.best_time_of_day)}</div>
                    </div>
                  )}
                  {place.crowd_level && (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">Crowd Level</div>
                      <div className="font-semibold text-blue-700 text-sm">{place.crowd_level}</div>
                    </div>
                  )}
                </div>
                {place.season_notes && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Seasonal Notes</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{place.season_notes}</p>
                  </div>
                )}
              </>
            )}

            {/* Pros and Cons - All Entity Types */}
            {(place.pros1 || place.pros2 || place.pros3 || place.cons1 || place.cons2 || place.cons3) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Pros */}
                {(place.pros1 || place.pros2 || place.pros3) && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2 text-sm">What's Great</h4>
                    <ul className="space-y-1.5">
                      {place.pros1 && (
                        <li className="text-sm text-gray-700 flex items-start">
                          <span className="text-green-600 mr-2 mt-0.5">✓</span>
                          <span>{place.pros1}</span>
                        </li>
                      )}
                      {place.pros2 && (
                        <li className="text-sm text-gray-700 flex items-start">
                          <span className="text-green-600 mr-2 mt-0.5">✓</span>
                          <span>{place.pros2}</span>
                        </li>
                      )}
                      {place.pros3 && (
                        <li className="text-sm text-gray-700 flex items-start">
                          <span className="text-green-600 mr-2 mt-0.5">✓</span>
                          <span>{place.pros3}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                
                {/* Cons */}
                {(place.cons1 || place.cons2 || place.cons3) && (
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-orange-800 mb-2 text-sm">Things to Note</h4>
                    <ul className="space-y-1.5">
                      {place.cons1 && (
                        <li className="text-sm text-gray-700 flex items-start">
                          <span className="text-orange-600 mr-2 mt-0.5">•</span>
                          <span>{place.cons1}</span>
                        </li>
                      )}
                      {place.cons2 && (
                        <li className="text-sm text-gray-700 flex items-start">
                          <span className="text-orange-600 mr-2 mt-0.5">•</span>
                          <span>{place.cons2}</span>
                        </li>
                      )}
                      {place.cons3 && (
                        <li className="text-sm text-gray-700 flex items-start">
                          <span className="text-orange-600 mr-2 mt-0.5">•</span>
                          <span>{place.cons3}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Registration Prompt */}
      <RegistrationPrompt
        isOpen={showRegistrationPrompt}
        onClose={() => setShowRegistrationPrompt(false)}
        variant="google_auth"
        trigger="save"
      />
    </>
  );
};

export default React.memo(PlaceDetailPanel);

